// The money on one car, receipt by receipt — and the handover at the end.
//
// A ₹10L car is normally paid for three times: a token holds it, the bank's
// disbursement lands weeks later, the rest arrives at the door. This modal is
// that whole story on one screen: every receipt (with its bank when a bank
// was involved), what is still owed, and — only once nothing is — the
// delivery, which produces the note the buyer takes home on WhatsApp.
//
// Receipts are added and removed, never edited: a wrong one comes out (into
// the superadmin's deletion ledger) and goes in again right.

import React, { useEffect, useState } from 'react';
import {
  FiCheckCircle, FiDownload, FiRotateCcw, FiSend, FiTrash2, FiTruck,
} from 'react-icons/fi';
import { Alert, Button } from '../common/index.js';
import { Field, Modal, NumberInput, Select, errText, fmtMoney, inputCls } from './controls.jsx';
import {
  CAR_PAYMENT_MODE_OPTIONS, MODE_NEEDS_BANK, carModeLabel,
} from '../../constants/paymentModes';
import { receiptCheck, remainingOn, waShareLink } from './carsMoney.js';
import {
  downloadBookingNote, downloadDeliveryNote, whatsAppBookingNote,
} from './noteActions.js';
import { carsTeamApi } from '../../api/carsApi';

const today = () => new Date().toISOString().slice(0, 10);

const emptyReceipt = () => ({
  amount: '', mode: 'cash', bank_name: '', paid_on: today(), note: '',
});

// ── One receipt row ──────────────────────────────────────────────────────────
const ReceiptRow = ({ payment, canRemove, busy, onRemove }) => {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-800">
            {carModeLabel(payment.mode)}
            {payment.bank_name && (
              <span className="font-semibold text-violet-700"> — {payment.bank_name}</span>
            )}
          </p>
          <p className="text-[11px] text-slate-400">
            {payment.paid_on_display}
            {payment.taken_by_name ? ` · ${payment.taken_by_name}` : ''}
            {payment.note ? ` · ${payment.note}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <p className="text-sm font-black tabular-nums text-slate-900">{fmtMoney(payment.amount)}</p>
          {canRemove && !confirming && (
            <button type="button" onClick={() => setConfirming(true)} title="Entered wrong? Remove it"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-600">
              <FiTrash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-2">
          <input
            className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
            placeholder="Why? (goes to the superadmin's deleted records)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button size="sm" variant="danger" disabled={busy}
            onClick={() => onRemove(payment, reason)}>
            Remove
          </Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
            Keep
          </Button>
        </div>
      )}
    </div>
  );
};

// ── The modal ────────────────────────────────────────────────────────────────
const CarMoneyModal = ({ open, car: carProp, onClose, onChanged }) => {
  // The modal keeps its own copy of the car and refreshes it from each
  // response — so recording the final receipt shows "ready to hand over"
  // right here, even if the card just left the page's current tab.
  const [car, setCar] = useState(carProp);
  const [receipt, setReceipt] = useState(emptyReceipt);
  const [aadhaar, setAadhaar] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCar(carProp);
    setReceipt(emptyReceipt());
    setAadhaar('');
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, carProp?.id]);

  const sale = car?.sale;
  if (!sale) return null;

  const remaining = remainingOn(sale);
  const owing = remaining > 0;
  const delivered = Boolean(sale.delivered);
  const set = (key) => (value) => setReceipt((f) => ({ ...f, [key]: value }));
  const needsBank = MODE_NEEDS_BANK.has(receipt.mode);
  const shareLink = waShareLink(car, sale);

  const run = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (res?.data?.id) setCar(res.data);    // every endpoint returns the fresh car
      onChanged();
    } catch (e) {
      setError(errText(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const addReceipt = () => {
    const check = receiptCheck(receipt, remaining);
    if (!check.ok) { setError(check.error); return; }
    run(async () => {
      const res = await carsTeamApi.addPayment(car.id, receipt);
      setReceipt(emptyReceipt());
      return res;
    }, 'Could not record the money.');
  };

  const removeReceipt = (payment, reason) =>
    run(() => carsTeamApi.removePayment(car.id, payment.id, reason),
      'Could not remove the receipt.');

  const deliver = () =>
    run(() => carsTeamApi.deliver(car.id, aadhaar.trim() ? { aadhaar: aadhaar.trim() } : {}),
      'Could not mark the delivery.');

  const undoDeliver = () =>
    run(() => carsTeamApi.undoDeliver(car.id), 'Could not undo the delivery.');

  // The same paper actions the row buttons use — one implementation.
  const paper = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      await fn(car);
    } catch (e) {
      setError(e?.response ? errText(e, fallback) : (e?.message || fallback));
    } finally {
      setBusy(false);
    }
  };
  const downloadNote = () => paper(downloadDeliveryNote, 'Could not fetch the delivery note.');
  const downloadBooking = () => paper(downloadBookingNote, 'Could not fetch the booking note.');
  const whatsAppBooking = () => paper(whatsAppBookingNote, 'Could not prepare the booking note.');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="The money on this car"
      subtitle={`${car.display_name} — ${sale.customer_name || 'the buyer'} · sold for ${fmtMoney(sale.sold_price)}`}
    >
      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      {/* Where the deal stands */}
      <div className={`rounded-2xl border p-3 ${
        owing ? 'border-amber-200 bg-amber-50/70' : 'border-emerald-200 bg-emerald-50/70'
      }`}>
        <div className="flex items-center justify-between">
          <p className={`text-[11px] font-black uppercase tracking-wide ${
            owing ? 'text-amber-700' : 'text-emerald-700'
          }`}>
            {owing ? 'Still to collect' : 'All money received'}
          </p>
          <p className={`text-lg font-black tabular-nums ${owing ? 'text-amber-700' : 'text-emerald-700'}`}>
            {owing ? fmtMoney(remaining) : fmtMoney(sale.sold_price)}
          </p>
        </div>
        <p className="text-[11px] font-semibold text-slate-500">
          {fmtMoney(sale.collected)} in hand of {fmtMoney(sale.sold_price)}
        </p>
      </div>

      {/* Every arrival so far */}
      <div className="mt-4 space-y-1.5">
        {(sale.payments || []).map((p) => (
          <ReceiptRow
            key={p.id}
            payment={p}
            busy={busy}
            canRemove={!delivered}
            onRemove={removeReceipt}
          />
        ))}
        {(sale.payments || []).length === 0 && (
          <p className="py-2 text-center text-xs text-slate-400">No receipts recorded on this sale.</p>
        )}
      </div>

      {/* More money arriving */}
      {owing && !delivered && (
        <div className="mt-4 rounded-2xl border border-slate-200 p-3">
          <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">
            Money arrived
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <Field label="Amount" required>
              <div className="flex gap-1.5">
                <NumberInput value={receipt.amount} onChange={set('amount')} placeholder="500000" />
                <button
                  type="button"
                  onClick={() => set('amount')(String(remaining))}
                  className="shrink-0 rounded-xl border border-violet-200 bg-violet-50 px-2.5 text-[11px] font-bold text-violet-700 transition hover:bg-violet-100"
                  title="The whole balance"
                >
                  All of it
                </button>
              </div>
            </Field>
            <Field label="How">
              <Select value={receipt.mode} onChange={set('mode')} options={CAR_PAYMENT_MODE_OPTIONS} />
            </Field>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {needsBank && (
              <Field
                label={receipt.mode === 'loan' ? 'Which bank / financer?' : 'Which bank?'}
                required={receipt.mode === 'loan'}
              >
                <input className={inputCls} value={receipt.bank_name}
                  onChange={(e) => set('bank_name')(e.target.value)} placeholder="HDFC Bank" />
              </Field>
            )}
            <Field label="On">
              <input type="date" className={inputCls} value={receipt.paid_on}
                onChange={(e) => set('paid_on')(e.target.value)} />
            </Field>
            {!needsBank && (
              <Field label="Note">
                <input className={inputCls} value={receipt.note}
                  onChange={(e) => set('note')(e.target.value)} placeholder="Optional" />
              </Field>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={addReceipt} loading={busy}>
              <FiCheckCircle className="mr-1.5 h-3.5 w-3.5" /> Record it
            </Button>
          </div>
        </div>
      )}

      {/* The handover */}
      <div className="mt-4">
        {delivered ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
            <p className="inline-flex items-center gap-1.5 text-sm font-black text-emerald-800">
              <FiTruck className="h-4 w-4" />
              Delivered {sale.delivered_at_display}
              {sale.delivered_by_name ? ` · by ${sale.delivered_by_name}` : ''}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {shareLink ? (
                <a href={shareLink} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-emerald-700">
                  <FiSend className="h-3.5 w-3.5" /> WhatsApp the note
                </a>
              ) : (
                <p className="text-[11px] font-semibold text-slate-400">
                  No phone on the sale — download the note and share it yourself.
                </p>
              )}
              <button type="button" onClick={downloadNote}
                className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-300 bg-white px-3 py-2 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100">
                <FiDownload className="h-3.5 w-3.5" /> Delivery note PDF
              </button>
              <button type="button" onClick={undoDeliver} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                <FiRotateCcw className="h-3.5 w-3.5" /> Not delivered yet
              </button>
            </div>
          </div>
        ) : owing ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-amber-700">
              Booked — send the buyer their confirmation
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" onClick={whatsAppBooking} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-emerald-700 disabled:opacity-50">
                <FiSend className="h-3.5 w-3.5" /> WhatsApp booking note
              </button>
              <button type="button" onClick={downloadBooking}
                className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-bold text-amber-700 transition hover:bg-amber-100">
                <FiDownload className="h-3.5 w-3.5" /> Booking PDF
              </button>
            </div>
            <p className="mt-2 text-[11px] text-amber-700/70">
              Delivery unlocks when the money is all in — {fmtMoney(remaining)} to go.
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-violet-700">
              Ready to hand over
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <Field label="Buyer's Aadhaar" hint="For the RC transfer — optional">
                <input
                  className={inputCls}
                  value={aadhaar}
                  onChange={(e) => setAadhaar(e.target.value)}
                  placeholder="12 digits, if they share it"
                  inputMode="numeric"
                />
              </Field>
              <div className="flex items-end">
                <Button onClick={deliver} loading={busy} className="w-full">
                  <FiTruck className="mr-1.5 h-4 w-4" /> Delivered — make the note
                </Button>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-violet-700/70">
              This writes the delivery note PDF, ready to WhatsApp to the buyer.
            </p>
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
      </div>
    </Modal>
  );
};

export default CarMoneyModal;
